const SHARED_COOKIE_DEFAULT_NAME = "office_shared_session";
const SHARED_COOKIE_DEFAULT_DOMAIN = ".uds.com.vn";
const DEFAULT_SESSION_SECONDS = 1800;
const DEFAULT_REMEMBER_SECONDS = 2592000;

function getRootApiUrl() {
  return (window.MyConfig && window.MyConfig.hostApi) || "";
}

function getConfiguredDefaultRouter() {
  return (window.MyConfig && window.MyConfig.defaultRouter) || "/admin/hello";
}

function sanitizeInternalPath(path, fallbackPath) {
  if (!path) return fallbackPath;

  try {
    var normalizedUrl = new URL(path, window.location.origin);
    if (normalizedUrl.origin !== window.location.origin) {
      return fallbackPath;
    }

    var normalizedPath =
      normalizedUrl.pathname + normalizedUrl.search + normalizedUrl.hash;

    if (!normalizedPath || normalizedPath.indexOf("/auth/login") === 0) {
      return fallbackPath;
    }

    return normalizedPath;
  } catch (error) {
    return fallbackPath;
  }
}

function getTokenStorageKey(rootApiUrl) {
  return "mushroom.tokens[" + rootApiUrl + "]";
}

function getExpiredStorageKey(rootApiUrl) {
  return getTokenStorageKey(rootApiUrl) + ".expired";
}

function getSharedAuthConfig() {
  return (window.MyConfig && window.MyConfig.sharedAuth) || {};
}

function getCookieName() {
  return getSharedAuthConfig().cookieName || SHARED_COOKIE_DEFAULT_NAME;
}

function getCookieDomain() {
  return getSharedAuthConfig().cookieDomain || SHARED_COOKIE_DEFAULT_DOMAIN;
}

function isSharedAuthEnabled() {
  return !!(getSharedAuthConfig() && getSharedAuthConfig().enabled);
}

function canUseSharedCookieDomain() {
  var cookieDomain = getCookieDomain();
  if (!cookieDomain) return false;

  var normalizedDomain = cookieDomain.replace(/^\./, "");
  var hostName = window.location.hostname;

  return (
    hostName === normalizedDomain ||
    hostName.endsWith("." + normalizedDomain)
  );
}

function readCookie(cookieName) {
  var cookiePrefix = encodeURIComponent(cookieName) + "=";
  var cookieItems = document.cookie ? document.cookie.split("; ") : [];

  for (var index = 0; index < cookieItems.length; index += 1) {
    if (cookieItems[index].indexOf(cookiePrefix) === 0) {
      return decodeURIComponent(cookieItems[index].substring(cookiePrefix.length));
    }
  }

  return "";
}

function writeCookie(cookieName, value, maxAgeSeconds) {
  if (!canUseSharedCookieDomain()) return false;

  var cookie = [
    encodeURIComponent(cookieName) + "=" + encodeURIComponent(value),
    "Path=/",
    "SameSite=Lax",
    "Domain=" + getCookieDomain(),
  ];

  if (typeof maxAgeSeconds === "number") {
    cookie.push("Max-Age=" + maxAgeSeconds);
  }

  if (window.location.protocol === "https:") {
    cookie.push("Secure");
  }

  document.cookie = cookie.join("; ");
  return true;
}

function deleteCookie(cookieName) {
  writeCookie(cookieName, "", 0);
}

function getCookiePayload() {
  if (!isSharedAuthEnabled()) return null;

  var rawValue = readCookie(getCookieName());
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    console.warn("Cannot parse shared auth cookie", error);
    return null;
  }
}

function setLocalToken(token, remember, expiresAt) {
  var rootApiUrl = getRootApiUrl();
  if (!rootApiUrl || !token) return false;

  localStorage.setItem(getTokenStorageKey(rootApiUrl), token);

  if (remember) {
    localStorage.removeItem(getExpiredStorageKey(rootApiUrl));
  } else if (expiresAt) {
    localStorage.setItem(getExpiredStorageKey(rootApiUrl), String(expiresAt));
  }

  return true;
}

function removeLocalToken() {
  var rootApiUrl = getRootApiUrl();
  if (!rootApiUrl) return;

  localStorage.removeItem(getTokenStorageKey(rootApiUrl));
  localStorage.removeItem(getExpiredStorageKey(rootApiUrl));
}

const SharedAuthService = {};

SharedAuthService.getDefaultRouter = () => {
  var fallbackPath = "/admin/hello";
  return sanitizeInternalPath(getConfiguredDefaultRouter(), fallbackPath);
};

SharedAuthService.getLocalToken = () => {
  var rootApiUrl = getRootApiUrl();
  if (!rootApiUrl) return "";
  return localStorage.getItem(getTokenStorageKey(rootApiUrl)) || "";
};

SharedAuthService.hasLocalToken = () => {
  return !!SharedAuthService.getLocalToken();
};

SharedAuthService.syncSharedCookie = (options = {}) => {
  if (!isSharedAuthEnabled()) return false;

  var token = options.token || SharedAuthService.getLocalToken();
  if (!token) return false;

  var remember =
    options.remember !== undefined
      ? options.remember
      : !localStorage.getItem(getExpiredStorageKey(getRootApiUrl()));
  var maxAgeSeconds = remember
    ? getSharedAuthConfig().rememberSeconds || DEFAULT_REMEMBER_SECONDS
    : getSharedAuthConfig().sessionSeconds || DEFAULT_SESSION_SECONDS;
  var expiresAt = Date.now() + maxAgeSeconds * 1000;

  return writeCookie(
    getCookieName(),
    JSON.stringify({
      token: token,
      remember: remember,
      rootApiUrl: getRootApiUrl(),
      expiresAt: expiresAt,
    }),
    maxAgeSeconds
  );
};

SharedAuthService.syncCurrentLocalTokenToCookie = (remember) => {
  return SharedAuthService.syncSharedCookie({ remember: remember });
};

SharedAuthService.hydrateLocalTokenFromSharedCookie = () => {
  if (SharedAuthService.hasLocalToken()) return false;

  var payload = getCookiePayload();
  if (!payload || !payload.token) return false;

  if (payload.rootApiUrl && payload.rootApiUrl !== getRootApiUrl()) {
    return false;
  }

  if (payload.expiresAt && payload.expiresAt < Date.now()) {
    SharedAuthService.clearSharedAuth();
    return false;
  }

  return setLocalToken(payload.token, payload.remember, payload.expiresAt);
};

SharedAuthService.clearSharedAuth = () => {
  removeLocalToken();
  if (isSharedAuthEnabled()) {
    deleteCookie(getCookieName());
  }
};

SharedAuthService.resolveRedirectPath = (redirectPath, fallbackPath) => {
  var safeFallbackPath = SharedAuthService.getDefaultRouter();

  if (fallbackPath) {
    safeFallbackPath = sanitizeInternalPath(
      fallbackPath,
      SharedAuthService.getDefaultRouter()
    );
  }

  return sanitizeInternalPath(redirectPath, safeFallbackPath);
};

SharedAuthService.buildSharedLoginUrl = (targetPath, source) => {
  var params = new URLSearchParams();
  params.set(
    "target",
    SharedAuthService.resolveRedirectPath(
      targetPath,
      SharedAuthService.getDefaultRouter()
    )
  );

  if (source) {
    params.set("source", source);
  }

  return (
    (getSharedAuthConfig().redirectPath || "/auth/shared-login") +
    "?" +
    params.toString()
  );
};

export default SharedAuthService;
